/**
 * @unscoped — AdminConsole 의 i18n — Cycle 37 → 41.
 *
 * Cycle 41 — base-i18n.ts 통합 helper 도입. lang state / storage / listener 는 base 가 단일 source.
 * 본 모듈은 MESSAGES 사전 + tAdmin 래퍼만 유지. setAdminLang/subscribeAdminLang 은 base 의 thin wrapper.
 *
 * (cycle 37-40 history: navigator detect, localStorage, drift sync, globalThis instance)
 */
import {
  getSharedLang, setSharedLang, subscribeSharedLang,
  FALLBACK_LANG, SHARED_LANG_STORAGE_KEY, type Lang,
  _setSharedLangForTests, _getSharedLangForTests,
} from './base-i18n';

// Cycle 41 — backward-compat export. 기존 import 자리 그대로 동작.
// 정책: localStorage key 는 cycle 38 의 LANG_STORAGE_KEY 와 동일 ('workchat:admin-lang').
const LANG_STORAGE_KEY = SHARED_LANG_STORAGE_KEY;

// Cycle 45 — ja (일본어) 분기 시작 (sidebar/group/tab/access_denied.heading/lang/module.label, 22 keys).
// Cycle 46 — 나머지 46 keys (overview/access_denied.body/llm/module/user) ja 완결 → 68/68 ko·en·ja 완비.
//   admin-i18n 이 ja 미완 마지막 모듈이었음. 본 cycle 로 third-lang 롤아웃 종료.
//   tAdmin 의 fallback chain (lang → en → ko) 은 향후 새 lang 추가 시 안전망으로 유지.
const MESSAGES = {
  // sidebar
  'admin.title': { ko: '관리 콘솔', en: 'Admin Console', ja: '管理コンソール' },
  'admin.sidebar.label': { ko: '관리 콘솔 메뉴', en: 'Admin console menu', ja: '管理コンソール メニュー' },
  'admin.sidebar.section_list_label': { ko: '섹션 목록', en: 'Sections', ja: 'セクション一覧' },
  'admin.group.env': { ko: '환경', en: 'Environment', ja: '環境' },
  'admin.group.integration': { ko: '통합', en: 'Integration', ja: '統合' },
  'admin.group.user': { ko: '사용자', en: 'Users', ja: 'ユーザー' },
  // tabs
  'admin.tab.overview': { ko: '개요', en: 'Overview', ja: '概要' },
  'admin.tab.llm': { ko: 'LLM 연동', en: 'LLM integration', ja: 'LLM 連携' },
  'admin.tab.modules': { ko: '서비스 토글', en: 'Service modules', ja: 'サービストグル' },
  'admin.tab.users': { ko: '사용자 권한', en: 'User roles', ja: 'ユーザー権限' },
  'admin.tab.user_create': { ko: '사용자 등록', en: 'Create user', ja: 'ユーザー登録' },
  'admin.tab.notify': { ko: '알림 설정', en: 'Notifications', ja: '通知設定' },
  // overview content (Cycle 46 — ja 추가)
  'admin.overview.heading': { ko: '개요', en: 'Overview', ja: '概要' },
  'admin.overview.current_admin_label': { ko: '현재 관리자:', en: 'Current admin:', ja: '現在の管理者:' },
  'admin.overview.item.llm': {
    ko: 'LLM 연동 — 외부 LLM (OpenAI / Anthropic / 기타) API 키 등록·관리',
    en: 'LLM integration — register/manage external LLM (OpenAI / Anthropic / others) API keys',
    ja: 'LLM 連携 — 外部LLM (OpenAI / Anthropic / その他) のAPIキーを登録・管理',
  },
  'admin.overview.item.modules': {
    ko: '서비스 토글 — 메일 / 캘린더 / 업무 / 워크스페이스 사용 여부 결정',
    en: 'Service modules — toggle mail / calendar / tasks / workspace usage',
    ja: 'サービストグル — メール / カレンダー / タスク / ワークスペースの使用可否を設定',
  },
  'admin.overview.item.users': {
    ko: '사용자 권한 — 일반 사용자(STAFF) 의 관리자(MANAGER) 권한 지정',
    en: 'User roles — promote STAFF users to MANAGER',
    ja: 'ユーザー権限 — 一般ユーザー(STAFF)に管理者(MANAGER)権限を付与',
  },
  'admin.overview.item.notify': {
    ko: '알림 설정 — drift 이메일 수신 거부',
    en: 'Notifications — opt out of drift emails',
    ja: '通知設定 — ドリフトメールの受信を拒否',
  },
  // access denied
  'admin.access_denied.heading': { ko: '접근 권한이 없습니다', en: 'Access denied', ja: 'アクセス権限がありません' },
  'admin.access_denied.body': {
    ko: '이 페이지는 회사 관리자(ADMIN) 만 접근할 수 있습니다.',
    en: 'This page is restricted to company administrators (ADMIN).',
    ja: 'このページは会社管理者(ADMIN)のみアクセスできます。',
  },
  // Cycle 38 — lang switcher
  'admin.lang.label': { ko: '언어', en: 'Language', ja: '言語' },
  'admin.lang.ko': { ko: '한국어', en: 'Korean', ja: '韓国語' },
  'admin.lang.en': { ko: 'English', en: 'English', ja: '英語' },
  // Cycle 44 — auto option (navigator default)
  'admin.lang.auto': { ko: '자동 (브라우저 설정)', en: 'Auto (browser default)', ja: '自動 (ブラウザ設定)' },
  // Cycle 45 — ja option
  'admin.lang.ja': { ko: '일본어', en: 'Japanese', ja: '日本語' },
  // Cycle 38 — LlmIntegrationSection 핵심 텍스트 (Cycle 46 — ja 추가)
  'admin.llm.heading': { ko: 'LLM 외부 연동', en: 'External LLM integrations', ja: 'LLM 外部連携' },
  'admin.llm.subheading': {
    ko: 'OpenAI / Anthropic / 기타 LLM 의 API 키를 등록합니다. 키는 AES-256-GCM 으로 암호화되어 저장되며 화면에는 일부만 표시됩니다.',
    en: 'Register API keys for OpenAI / Anthropic / other LLMs. Keys are encrypted with AES-256-GCM and only partially shown.',
    ja: 'OpenAI / Anthropic / その他のLLMのAPIキーを登録します。キーはAES-256-GCMで暗号化されて保存され、画面には一部のみ表示されます。',
  },
  'admin.llm.add_button': { ko: '+ 새 LLM 연동 추가', en: '+ Add new LLM integration', ja: '+ 新しいLLM連携を追加' },
  'admin.llm.save_button': { ko: '저장', en: 'Save', ja: '保存' },
  'admin.llm.edit_button': { ko: '수정', en: 'Edit', ja: '編集' },
  'admin.llm.cancel_button': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },
  'admin.llm.delete_button': { ko: '삭제', en: 'Delete', ja: '削除' },
  'admin.llm.list.empty': { ko: '등록된 LLM 연동이 없습니다.', en: 'No LLM integrations registered.', ja: '登録されたLLM連携がありません。' },
  'admin.llm.loading': { ko: '불러오는 중...', en: 'Loading...', ja: '読み込み中...' },
  'admin.llm.list.aria_label': { ko: 'LLM 연동 목록', en: 'LLM integrations list', ja: 'LLM 連携一覧' },
  // Cycle 39 — LLM form label/placeholder 완전 i18n (Cycle 46 — ja 추가)
  'admin.llm.form.provider': { ko: '제공자', en: 'Provider', ja: 'プロバイダー' },
  'admin.llm.form.endpoint': { ko: '엔드포인트', en: 'Endpoint', ja: 'エンドポイント' },
  'admin.llm.form.apikey': { ko: 'API 키', en: 'API key', ja: 'APIキー' },
  'admin.llm.form.apikey_edit_hint': { ko: '(변경 시만 입력)', en: '(only fill to change)', ja: '(変更する場合のみ入力)' },
  'admin.llm.form.apikey_keep_hint': { ko: '비워두면 기존 키 유지', en: 'Leave blank to keep existing key', ja: '空欄の場合は既存のキーを保持' },
  'admin.llm.form.model': { ko: '모델명', en: 'Model name', ja: 'モデル名' },
  'admin.llm.form.displayname': { ko: '표시명', en: 'Display name', ja: '表示名' },
  'admin.llm.form.displayname_placeholder': { ko: '예: 우리 회사 OpenAI', en: 'e.g., Our Company OpenAI', ja: '例: 自社のOpenAI' },
  'admin.llm.form.workspaceid': { ko: 'Workspace/Org ID (선택)', en: 'Workspace/Org ID (optional)', ja: 'Workspace/Org ID (任意)' },
  'admin.llm.form.workspaceid_placeholder': {
    ko: 'anthropic: workspace_xxx / openai: org-xxx (billing 매핑용)',
    en: 'anthropic: workspace_xxx / openai: org-xxx (for billing mapping)',
    ja: 'anthropic: workspace_xxx / openai: org-xxx (billingマッピング用)',
  },
  // Cycle 39 — ServiceModuleSection 핵심 i18n (Cycle 46 — ja 추가)
  'admin.module.heading': { ko: '서비스 토글', en: 'Service modules', ja: 'サービストグル' },
  'admin.module.subheading': {
    ko: '회사에서 사용할 서비스를 켜고 끕니다. 비활성화하면 사용자 메뉴에서 사라집니다.',
    en: 'Toggle services for your company. Disabled services disappear from user menus.',
    ja: '会社で使用するサービスのオン/オフを切り替えます。無効にするとユーザーメニューから消えます。',
  },
  'admin.module.list.aria_label': { ko: '서비스 모듈 목록', en: 'Service modules list', ja: 'サービスモジュール一覧' },
  'admin.module.col.service': { ko: '서비스', en: 'Service', ja: 'サービス' },
  'admin.module.col.status': { ko: '상태', en: 'Status', ja: '状態' },
  'admin.module.col.updated': { ko: '마지막 변경', en: 'Last update', ja: '最終更新' },
  'admin.module.status.enabled': { ko: '✓ 활성', en: '✓ Enabled', ja: '✓ 有効' },
  'admin.module.status.disabled': { ko: '비활성', en: 'Disabled', ja: '無効' },
  'admin.module.action.enable': { ko: '활성화', en: 'Enable', ja: '有効化' },
  'admin.module.action.disable': { ko: '비활성화', en: 'Disable', ja: '無効化' },
  // Cycle 39 — UserRoleSection 핵심 i18n (Cycle 46 — ja 추가)
  'admin.user.heading': { ko: '사용자 권한 변경', en: 'Change user role', ja: 'ユーザー権限の変更' },
  'admin.user.subheading': {
    ko: '일반 사용자(STAFF) ↔ 관리자(MANAGER) 권한 변경. 사용자 ID 는 사용자 상세 화면에서 확인할 수 있습니다.',
    en: 'Switch users between STAFF and MANAGER. User IDs are available on user detail screens.',
    ja: '一般ユーザー(STAFF) ↔ 管理者(MANAGER)の権限を変更します。ユーザーIDはユーザー詳細画面で確認できます。',
  },
  'admin.user.form.userid': { ko: '사용자 ID', en: 'User ID', ja: 'ユーザーID' },
  'admin.user.form.newrole': { ko: '새 권한', en: 'New role', ja: '新しい権限' },
  'admin.user.form.submit': { ko: '권한 변경', en: 'Update role', ja: '権限を変更' },
  'admin.user.form.submit_busy': { ko: '변경 중...', en: 'Updating...', ja: '変更中...' },
  // Cycle 40 — UserRoleSection select option + disclaimer (Cycle 46 — ja 추가)
  'admin.user.role.staff': { ko: 'STAFF (일반 사용자)', en: 'STAFF (regular user)', ja: 'STAFF (一般ユーザー)' },
  'admin.user.role.manager': { ko: 'MANAGER (관리자)', en: 'MANAGER (administrator)', ja: 'MANAGER (管理者)' },
  'admin.user.disclaimer': {
    ko: '(다음 업데이트에서 사용자 검색 + 일괄 변경 화면이 추가됩니다.)',
    en: '(User search + batch update UI will arrive in a future update.)',
    ja: '(次回のアップデートでユーザー検索 + 一括変更画面が追加されます。)',
  },
  // Cycle 40 — ServiceModuleSection MODULE_LABELS
  // Cycle 45 — ja 추가
  'admin.module.label.mail': { ko: '메일', en: 'Mail', ja: 'メール' },
  'admin.module.label.calendar': { ko: '캘린더', en: 'Calendar', ja: 'カレンダー' },
  'admin.module.label.task': { ko: '업무', en: 'Tasks', ja: 'タスク' },
  'admin.module.label.workspace': { ko: '워크스페이스', en: 'Workspace', ja: 'ワークスペース' },
  'admin.module.label.workchat': { ko: '채팅', en: 'Chat', ja: 'チャット' },
} as const;

type Key = keyof typeof MESSAGES;

// Cycle 41 — base-i18n.ts 의 단일 globalThis state 사용. 자체 state 제거.
// 모든 lang read/write 가 base 의 getSharedLang/setSharedLang/subscribeSharedLang 위임.

export function tAdmin(key: Key): string {
  const m = MESSAGES[key] as Record<string, string> | undefined;
  if (!m) return key;
  // Cycle 45 — ja 등 일부 lang 미정의 시 en → FALLBACK_LANG 순으로 fallback.
  // Cycle 48 — fallback 의 마지막 분기까지 모두 undefined 일 가능성 차단 (noUncheckedIndexedAccess
  // 환경에서 m[FALLBACK_LANG] 이 string | undefined 추론). 최종 fallback 으로 key 자체 반환.
  return m[getSharedLang()] ?? m['en'] ?? m[FALLBACK_LANG] ?? key;
}

export function getAdminLang(): Lang {
  return getSharedLang();
}

// Cycle 38 → 41 — 사용자 선택 lang 변경 API. base 의 setSharedLang thin wrapper.
// drift-i18n 도 같은 base state 사용 — 별도 sync 호출 불필요 (cycle 39 의 trick 제거).
export function setAdminLang(value: Lang): void {
  setSharedLang(value);
}

export function subscribeAdminLang(fn: () => void): () => void {
  return subscribeSharedLang(fn);
}

export function _setAdminLangForTests(value: Lang): void {
  _setSharedLangForTests(value);
}

export function _getAdminLangForTests(): Lang {
  return _getSharedLangForTests();
}

// Cycle 38 — 정식 storage key export (test localStorage 검증 + 외부 마이그레이션).
export const ADMIN_LANG_STORAGE_KEY = LANG_STORAGE_KEY;
